<template>
  <div class="job-finder">
    <div class="filter-container">
      <h3 class="title">
        {{ translate('search') }}
      </h3>
      <div class="input-selects-container">
        <div
          v-for="filter in filters"
          :key="filter.id"
          class="input-select"
          :class="{
            '-has-value': filter.value
          }"
        >
          <select
            v-if="filter.options.length"
            :id="filter.id"
            v-model="filter.value"
            :name="filter.name"
            class="select"
            @change="performSearch()"
          >
            <option
              value=""
              class="option"
              selected
            >
              {{ filter.name }}
            </option>

            <option
              v-for="(option) in filter.options"
              :key="option.id"
              :value="option.id"
              class="option"
            >
              {{ option.name }}
            </option>
          </select>
          <label
            class="label"
            :for="filter.id"
          >
            {{ filter.name }}
          </label>
        </div>
      </div>
      <form
        class="form-container"
        @submit.prevent="performSearch()"
      >
        <input
          v-model="searchTerm"
          class="input"
          type="text"
          :placeholder="translate('keyword')"
        >
        <button
          class="button button-primary"
          type="submit"
        >
          {{ translate('search') }}
        </button>
      </form>
    </div>
    <div
      v-if="!jobsLoading && totalJobs !== 0"
      class="result-count"
    >
      <p>{{ totalJobs }} {{ translate('jobs-found') }}</p>
    </div>
    <div
      v-else-if="!jobsLoading"
      class="empty-container"
    >
      <h4 class="title">
        {{ translations.noResults }}
      </h4>
      <div class="box">
        <p
          class="body"
          v-html="translations.infoBox"
        />
      </div>
    </div>
    <ul
      v-if="totalJobs !== 0"
      id="result-list"
      class="result-list -visible"
    >
      <li
        v-for="(job, index) in jobs"
        :key="`job.id-${index}`"
        class="item"
      >
        <a
          class="job-item"
          :href="job.links.directlink"
          target="_blank"
        >
          <span class="tag">{{ job.attributes['10'][0] }}</span>
          <p class="title">
            {{ job.title }}
          </p>
          <p class="description">
            {{ job.attributes['20'][0] }} ·
            <template v-if="job.szas['sza_pensum.min'] === '100'">
              {{ job.szas['sza_pensum.max'] }}% ·
            </template>
            <template v-else>
              {{ job.szas['sza_pensum.min'] }} - {{ job.szas['sza_pensum.max'] }}% ·
            </template>
            {{ job.szas['sza_employment_type'] }}
          </p>
          <span class="link button-tertiary">
            {{ translations.show_job }}
          </span>
        </a>
      </li>
      <template v-if="jobsLoading">
        <li
          v-for="n in limit"
          :key="`jobsLoading-item-${n}`"
          class="item"
        >
          <a
            class="job-item skeleton"
            href="#"
          >
            <span class="tag">Tag-Placeholder</span>
            <p class="title">
              Title-Placeholder
            </p>
            <p class="description">
              Description
            </p>
            <span class="link">
              {{ translations.show_job }}
            </span>
          </a>
        </li>
      </template>
    </ul>
    <div
      v-if="jobs.length"
      class="pagination-container"
    >
      <p>
        {{ translate('result-info', { count: jobs.length, total: totalJobs }) }}
      </p>
      <button
        v-if="canLoadMore"
        class="more button-secondary"
        @click="loadMore()"
      >
        {{ translate('load-more') }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'JobFinder',
  props: {
    language: {
      type: String,
      default: 'de',
    },
    translations: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      jobsLoading: false,
      filterLoading: false,
      offset: 0,
      totalJobs: 0,
      filters: [],
      jobs: [],
      searchTerm: '',
      currentSearchConfig: {
        searchTerm: '',
        filters: [],
      },
    };
  },
  computed: {
    limit() {
      return 10;
    },
    canLoadMore() {
      return this.jobs.length < this.totalJobs;
    },
    selectedFilters() {
      const filters = [];
      this.filters.forEach((f) => {
        if (f.value) {
          filters.push(`${f.id}:${f.value}`);
        }
      });
      return filters;
    },
    searchApiUrl() {
      let url = `/api/jobs/jobs?lang=${this.language}&limit=${this.limit}&offset=${this.offset}`;
      if (this.currentSearchConfig.searchTerm) {
        url += `&q=${this.currentSearchConfig.searchTerm}`;
      }
      if (this.currentSearchConfig.filters.length) {
        url += `&f=${this.currentSearchConfig.filters.join('&f=')}`;
      }
      return url;
    },
  },
  mounted() {
    this.fetchJobs();
    this.fetchFilters();
  },
  methods: {
    async fetchJobs() {
      try {
        this.jobsLoading = true;
        const response = await fetch(this.searchApiUrl);
        const data = await response.json();
        this.totalJobs = data.total;
        this.jobs.push(...data.jobs);
      } finally {
        this.jobsLoading = false;
      }
    },
    async fetchFilters() {
      try {
        this.filterLoading = true;
        const response = await fetch(`/api/jobs/capabilities?lang=${this.language}`);
        const filters = await response.json();
        const filtersMapped = filters.attributes
          .map((filter) => {
            let options = [];
            if (filter.values) {
              const keys = Object.keys(filter.values);
              options = keys.map((key) => ({
                id: key,
                name: filter.values[key],
              }));
            }
            return {
              id: filter.id,
              name: filter.name,
              value: '',
              options,
            };
          });
        // remove the "Berufsprofil" filter! @see POR-2274
        this.filters = filtersMapped.filter((obj) => (obj.id !== '30') && (obj.id !== 'experience-levels'));
      } catch (error) {
        this.filters = [];
      } finally {
        this.filterLoading = false;
      }
    },
    loadMore() {
      this.offset += this.limit;
      this.fetchJobs();
    },
    performSearch() {
      this.jobs = [];
      this.offset = 0;
      this.currentSearchConfig = {
        searchTerm: this.searchTerm,
        filters: [...this.selectedFilters],
      };
      this.fetchJobs();
    },
    translate(localeKey, options = null) {
      let string = this.translations[localeKey];
      if (!string) {
        return '';
      }
      if (options) {
        Object.keys(options).forEach((key) => {
          string = string.replace(`{${key}}`, options[key]);
        });
      }
      return string;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../sass/styles.scss";

.job-finder {
  --background-color: #FDFDFD;
  --border-color: #DFE1E2;
  padding: clamp(2rem, calc(5vw + 1rem), 5.4rem) 0;
}

.filter-container {
  margin-bottom: clamp(3.3rem, calc(5vw + 1rem), 6.6rem);

  > .title {
    font-size: 1rem;
    margin-bottom: 1rem;
  }
}

.result-list {
  display: grid;
  gap: 2rem;
  list-style: none;
  margin: 0;
  margin-top: 2rem;
  padding: 0 !important;
  height: 0;
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;

  &.-visible {
    height: auto;
    opacity: 1;
    visibility: visible;
  }

  > .item {
    list-style: none;

    &::before {
      display: none;
    }
  }
}

.job-item {
  display: grid;
  --paddingBlock: clamp(0.5rem, calc(2vw + 0.5rem), 2rem);
  --paddingInline: clamp(0.75rem, calc(2vw + 0.75rem), 2.5rem);
  padding: var(--paddingBlock) var(--paddingInline);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.12);
  text-decoration: none;

  > .tag {
    padding: 6px 12px;
    background: rgba(180, 175, 130, 0.2);
    width: fit-content;
  }

  > .title {
    font-size: clamp(1.25rem, calc(1vw + 0.5rem), 1.75rem);
    font-weight: 600;
    margin-top: clamp(2rem, calc(2vw + 0.75rem), 3rem);
  }

  > .description {
    margin-top: 1.125rem;
    width: auto;
  }

  > .link {
    margin-top: 1.5rem;
    justify-self: end;
    font-weight: 600;
    width: max-content;
  }

  @media (hover: hover) {
    &:hover > .link:after {
      background-size: 100% 100%;
    }
  }
}

.input-selects-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
}

.input-select {
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;

  > .label {
    pointer-events: none;
    font-family: $base-font-family;
    align-self: center;
    grid-column: 1;
    grid-row: 1;
    color: rgba($color-primary-blue, 0.7);
    font-weight: 600;
    z-index: 1;
    padding: 16px 54px 16px 24px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:not(.-has-value) {
    > .select {
      color: transparent;
    }
  }

  &.-has-value {
    > .label {
      visibility: visible;
      position: absolute;
      margin-left: 0.5rem;
      top: -0.5rem;
      left: 0px;
      font-size: 12px;
      background-color: var(--background-color);
      padding: 0 0.5rem;
      z-index: unset;
    }
  }

  > .select {
    position: relative;
    font-family: $base-font-family;
    grid-column: 1;
    grid-row: 1;
    padding: 16px 54px 16px 24px;
    width: 100%;
    background-color: var(--background-color, white);
    border: 2px solid var(--border-color);
    --color: #{$color-primary-blue};
    color: rgba(var(--color, black), 0.7);
    font-weight: 300;
    outline: none;

    // Remove default arrow
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;

    &:focus,
    &:focus-within {
      border: 2px solid rgba($color-primary-blue, 0.7);
    }

    > .option {
      color: rgba($color-primary-blue, 0.7);
    }

  }

  &::after {
    position: absolute;
    top: 50%;
    right: 2px;
    content: "";
    display: inline-block;
    width: 54px;
    height: 54px;
    pointer-events: none;
    transform: translateY(-50%);
    background-color: rgba($color-primary-blue, 0.7);
    mask-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
    mask-repeat: no-repeat;
    mask-position: center;
    mask-size: 24px;
  }
}
.form-container {
  display: grid;
  gap: 2rem;
  margin-top: 2rem;
  font-size: 18px;

  @include media(">=lg") {
    grid-template-columns: 1fr max-content;
  }

  > .input {
    padding: 1rem 1.5rem;
    color: rgba($color-primary-blue, 0.7);
    font-weight: 300;
    width: 100%;
    background-color: var(--background-color);
    border: 2px solid var(--border-color);
    font-family: $base-font-family;
    outline: none;

    &::placeholder {
      font-weight: 600;
      color: rgba($color-primary-blue, 0.7);
    }

    &:focus,
    &:focus-within {
      border: 2px solid rgba($color-primary-blue, 0.7);
    }
  }

  > .button {
    outline: none;
    width: max-content;
    justify-self: right;

    &:focus,
    &:focus-within {
      background-color: $color-blau-hover;
    }
  }
}

.skeleton {
  @keyframes phAnimation {
    0% {
      transform: translate3d(-30%, 0, 0);
    }
    100% {
      transform: translate3d(30%, 0, 0);
    }
  }

  position: relative;
  background-color: white;
  list-style: none;
  margin: 0;
  overflow: hidden;

  > * {
    background: #d9d9d9!important;
    color: #d9d9d9;
  }

  &::before {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: -50%;
    z-index: 1;
    width: 200%;
    background: linear-gradient(
      90deg,
      rgba(0,0,0,0) 40%,
      rgba(255,255,255, 0.5) 50%,
      rgba(0,0,0,0) 60%
    );
    animation: phAnimation 1s linear infinite;
    content: "";
    pointer-events: none;
  }
}

.pagination-container {
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-top: 3.1rem;
}

.empty-container {

  > .title {
    font-weight: 600;
    font-size: 1rem;
    margin-bottom: 24px;
  }

  > .box {
    display: grid;
    gap: 1rem;
    padding: 1.5rem;
    background-color: #fafafa;

    @include media(">=md") {
      gap: 2rem;
      grid-template-columns: 33px 1fr;
    }

    &::before {
      align-self: center;
      content: '';
      display: inline-flex;
      width: 33px;
      height: 33px;
      background-image: url("data:image/svg+xml,%3Csvg width='30' height='31' viewBox='0 0 30 31' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='15' cy='15.5' r='13' stroke='%2300376E' stroke-width='2'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M16 13.5L16 22.5L14 22.5L14 13.5L16 13.5Z' fill='%2300376E'/%3E%3Ccircle cx='15' cy='10' r='1.5' fill='%2300376E'/%3E%3C/svg%3E%0A");
      background-repeat: no-repeat;
      background-position: center;
      background-size: 33px;
    }
  }
}

</style>
